import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const BUCKET = "profile-pictures";

/**
 * Extract the storage path from a value that may be either:
 *   - already a relative path (e.g. "<uid>/profile_123.jpg") — returned as-is
 *   - a legacy full public URL (".../object/public/profile-pictures/<uid>/...")
 *   - a previously-signed URL ("...?token=...") — strips the query and slices
 *
 * Returns null if the input doesn't look like a profile-pictures object.
 */
export function extractPhotoPath(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  // Already a path — no protocol, no leading slash from our bucket marker
  if (!trimmed.includes("://") && !trimmed.startsWith("/")) {
    // Defensive: drop any querystring just in case
    const qIdx = trimmed.indexOf("?");
    return qIdx >= 0 ? trimmed.slice(0, qIdx) : trimmed;
  }

  // Full URL form — find the bucket marker and slice everything after it.
  // Works for both /object/public/profile-pictures/... and /object/sign/profile-pictures/...
  const marker = `/${BUCKET}/`;
  const idx = trimmed.indexOf(marker);
  if (idx < 0) return null;
  let path = trimmed.slice(idx + marker.length);
  const qIdx = path.indexOf("?");
  if (qIdx >= 0) path = path.slice(0, qIdx);
  return path || null;
}

/**
 * Mint a signed URL for a profile-pictures object.
 *
 * Accepts either a storage path or a legacy full public URL — we extract
 * the path internally so call sites don't need to know which shape they have.
 *
 * Returns null on any failure (no auth, RLS denial, file missing, etc.).
 * Callers should fall back to a placeholder/initials avatar when null.
 */
export async function getSignedPhotoUrl(
  pathOrUrl: string | null | undefined,
  ttlSeconds = 3600,
): Promise<string | null> {
  const path = extractPhotoPath(pathOrUrl);
  if (!path) return null;

  try {
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(path, ttlSeconds);
    if (error || !data?.signedUrl) {
      return null;
    }
    return data.signedUrl;
  } catch {
    return null;
  }
}

/**
 * Resolve a list of paths/URLs to signed URLs in a single round-trip when
 * possible. Order is preserved; entries that fail resolve to null.
 */
export async function getSignedPhotoUrls(
  pathsOrUrls: Array<string | null | undefined>,
  ttlSeconds = 3600,
): Promise<Array<string | null>> {
  const results = await Promise.all(
    pathsOrUrls.map((p) => getSignedPhotoUrl(p, ttlSeconds)),
  );
  return results;
}

/**
 * React hook: given a path-or-URL, returns a signed URL (or undefined while loading,
 * null on failure). Re-runs whenever the input changes.
 *
 * `undefined` distinguishes "still loading / never started" from `null`
 * ("attempted, failed/no input"). Components can pass `signed ?? undefined`
 * to <img src> / <AvatarImage src> so the fallback shows during load.
 */
export function useSignedPhotoUrl(
  pathOrUrl: string | null | undefined,
  ttlSeconds = 3600,
): string | null | undefined {
  const [signed, setSigned] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    if (!pathOrUrl) {
      setSigned(null);
      return;
    }
    setSigned(undefined);
    getSignedPhotoUrl(pathOrUrl, ttlSeconds).then((url) => {
      if (!cancelled) setSigned(url);
    });
    return () => {
      cancelled = true;
    };
  }, [pathOrUrl, ttlSeconds]);

  return signed;
}

/**
 * React hook variant for arrays (gallery photos). Returns an array of the same
 * length, each entry being a signed URL or null. Entries are `undefined` while
 * the batch is still loading.
 */
export function useSignedPhotoUrls(
  pathsOrUrls: ReadonlyArray<string | null | undefined> | null | undefined,
  ttlSeconds = 3600,
): Array<string | null> | undefined {
  const [signed, setSigned] = useState<Array<string | null> | undefined>(undefined);
  // Memoize on the joined string so we don't refire on identity-only changes.
  const key = pathsOrUrls ? pathsOrUrls.map((p) => p ?? "").join("|") : "";

  useEffect(() => {
    let cancelled = false;
    if (!pathsOrUrls || pathsOrUrls.length === 0) {
      setSigned([]);
      return;
    }
    setSigned(undefined);
    getSignedPhotoUrls(Array.from(pathsOrUrls), ttlSeconds).then((urls) => {
      if (!cancelled) setSigned(urls);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ttlSeconds]);

  return signed;
}
